import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { X, Layers, Tag } from "lucide-react";
import CreatableCombobox from "../../../components/CreatableCombobox";
import {
  getVendor,
  searchCategories,
  searchBrands,
  setVendorSubcategories,
  setVendorSubcategoryBrands,
} from "../../../services/materialAdmin";

/**
 * Per-vendor "material schema" coverage editor (slide-over).
 *
 * Section A — the sub-categories this vendor serves (a sub-category is a material_category with a
 * parent). Section B — for each served sub-category, the brands the vendor stocks there. These drive
 * tender vendor-matching downstream. Each change is persisted immediately (replace-set semantics).
 */
const VendorCoverageDrawer = ({ vendor, onClose }) => {
  const vendorId = vendor?.vendorId;
  const [coverage, setCoverage] = useState([]); // [{ vendorSubcategoryId, materialCategoryId, categoryName, brands: [{materialBrandId, brandName}] }]
  const [subcatOptions, setSubcatOptions] = useState([]); // sub-categories (parent != null)
  const [subcatParent, setSubcatParent] = useState({}); // materialCategoryId -> parentCategoryId
  const [brandOptions, setBrandOptions] = useState([]); // [{ value, label, categoryIds: [topLevelCatId] }]
  const [loading, setLoading] = useState(true);
  const [savingSubcats, setSavingSubcats] = useState(false);
  const [savingBrandsFor, setSavingBrandsFor] = useState(null); // vendorSubcategoryId in flight

  const load = useCallback(async () => {
    if (!vendorId) return;
    setLoading(true);
    try {
      const [vendorRes, catRes, brandRes] = await Promise.all([
        getVendor(vendorId),
        searchCategories({ pageSize: 500, withAttributes: false }),
        searchBrands({ pageSize: 500, withCategories: true }),
      ]);
      setCoverage(vendorRes?.subcategories || []);
      const cats = catRes?.items || (Array.isArray(catRes) ? catRes : []);
      setSubcatOptions(
        cats
          .filter((c) => c.parentCategoryId != null) // sub-categories only
          .map((c) => ({ value: c.materialCategoryId, label: c.categoryName }))
      );
      // Map each sub-category to its parent (top-level) category — brands are associated to the
      // top-level category, so a sub-category's relevant brands are its parent's brands.
      const parentMap = {};
      cats.forEach((c) => { parentMap[c.materialCategoryId] = c.parentCategoryId; });
      setSubcatParent(parentMap);
      const brands = brandRes?.items || (Array.isArray(brandRes) ? brandRes : []);
      setBrandOptions(
        brands.map((b) => ({
          value: b.materialBrandId,
          label: b.brandName,
          categoryIds: (b.categories || []).map((c) => c.materialCategoryId),
        }))
      );
    } catch (e) {
      toast.error("Failed to load vendor coverage");
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => { load(); }, [load]);

  const handleSetSubcats = async (materialCategoryIds) => {
    setSavingSubcats(true);
    try {
      const res = await setVendorSubcategories(vendorId, materialCategoryIds);
      setCoverage(res?.subcategories || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update served sub-categories");
    } finally {
      setSavingSubcats(false);
    }
  };

  const handleSetBrands = async (vendorSubcategoryId, materialBrandIds) => {
    setSavingBrandsFor(vendorSubcategoryId);
    try {
      const updated = await setVendorSubcategoryBrands(vendorId, vendorSubcategoryId, materialBrandIds);
      setCoverage((prev) =>
        prev.map((c) => (c.vendorSubcategoryId === vendorSubcategoryId ? updated : c))
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update brands");
    } finally {
      setSavingBrandsFor(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/50" onMouseDown={onClose}>
      <div
        className="h-full w-full max-w-xl bg-white shadow-xl flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-slate-900 truncate">Coverage — {vendor?.vendorName}</h3>
            <p className="text-xs text-slate-500">Which sub-categories this vendor serves, and the brands it stocks in each.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {loading ? (
            <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-10 rounded-xl bg-slate-100 animate-pulse" />)}</div>
          ) : (
            <>
              {/* Section A — served sub-categories */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Layers size={15} className="text-flamingo-600" />
                  <p className="text-sm font-semibold text-slate-900">Served sub-categories</p>
                </div>
                <CreatableCombobox
                  isMulti
                  value={coverage.map((c) => c.materialCategoryId)}
                  options={subcatOptions}
                  onChange={handleSetSubcats}
                  isDisabled={savingSubcats}
                  isLoading={savingSubcats}
                  placeholder="Select the sub-categories this vendor supplies…"
                />
                {subcatOptions.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1.5">No sub-categories exist yet — create them under the Categories tab (a sub-category is a category with a parent).</p>
                )}
              </div>

              {/* Section B — brands per served sub-category */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Tag size={15} className="text-flamingo-600" />
                  <p className="text-sm font-semibold text-slate-900">Available brands per sub-category</p>
                </div>
                {coverage.length === 0 ? (
                  <p className="text-xs text-slate-500">Add sub-categories above, then choose the brands stocked in each.</p>
                ) : (
                  <div className="space-y-4">
                    {coverage.map((c) => {
                      // Only offer brands linked to this sub-category's parent (top-level) category.
                      const parentId = subcatParent[c.materialCategoryId];
                      const opts = brandOptions.filter((b) => b.categoryIds.includes(parentId));
                      return (
                        <div key={c.vendorSubcategoryId} className="rounded-xl border border-slate-200 p-3">
                          <p className="text-xs font-semibold text-slate-700 mb-1.5">{c.categoryName}</p>
                          <CreatableCombobox
                            isMulti
                            value={(c.brands || []).map((b) => b.materialBrandId)}
                            options={opts}
                            onChange={(ids) => handleSetBrands(c.vendorSubcategoryId, ids)}
                            isDisabled={savingBrandsFor === c.vendorSubcategoryId}
                            isLoading={savingBrandsFor === c.vendorSubcategoryId}
                            placeholder="Brands this vendor stocks here…"
                          />
                          {opts.length === 0 && (
                            <p className="text-xs text-amber-600 mt-1.5">No brands linked to this category yet — add them under the Brands tab.</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end px-5 py-4 bg-slate-50 border-t border-slate-200">
          <button onClick={onClose} className="rounded-xl bg-flamingo-600 hover:bg-flamingo-700 text-white text-sm font-semibold px-5 py-2.5">Done</button>
        </div>
      </div>
    </div>
  );
};

export default VendorCoverageDrawer;
